import merge from 'lodash/merge';
import pick from 'lodash/pick';
import getSplashApiClient from 'lib/SplashApiClient/singleton';
import { decamelizeKeys } from 'humps';

const PAGINATION_KEYS = ['page', 'limit'];

export const VIEW_GROUP_BASIC = 'sessionBasic';
export const VIEW_GROUP_DETAILS = 'sessionDetails';
export const VIEW_GROUP_ATTENDEES_COUNT = 'attendeesCount';
export const VIEW_GROUP_TAGS = 'sessionTags';

export const VIEW_GROUPS = [VIEW_GROUP_BASIC, VIEW_GROUP_DETAILS, VIEW_GROUP_ATTENDEES_COUNT, VIEW_GROUP_TAGS];

export default class SessionApiClient {
    /**
     * @param {SplashApiClient} apiClient
     */
    constructor(apiClient) {
        this.apiClient = apiClient;
    }

    /**
     * Sends a GET request to retrieve sessions for the given event id.
     * Paginated set of results based on the pagination argument.
     *
     * @param {integer} eventId
     * @param {object} pagination
     * @param {array} viewGroups
     * @param {object} queryParams - any additional parameters to be added to the request
     */
    getSessions(eventId, pagination = { page: 1, limit: 10 }, viewGroups = [VIEW_GROUP_BASIC], queryParams = {}) {
        const paginationParams = pick(pagination, PAGINATION_KEYS);
        const requestParams = merge(paginationParams, { viewGroups }, decamelizeKeys(queryParams));
        return this.apiClient.get(`/event/${eventId}/sessions`, requestParams);
    }

    /**
     * Sends a GET request to retrieve sessions for the given event id and splash feed row.
     * This is used by the CMS session element to retrieve sessions for the page while
     * logged out.
     *
     * @param {integer} eventId
     * @param {integer} splashFeedId
     * @param {object} pagination
     * @param {array} viewGroups
     * @param {object} queryParams - any additional parameters to be added to the request
     * @param {string} guestAccessHash
     */
    getSessionsBySplashFeedId(
        eventId,
        splashFeedId,
        pagination = { page: 1, limit: 10 },
        viewGroups = [VIEW_GROUP_BASIC],
        queryParams = {},
        guestAccessHash,
    ) {
        const paginationParams = pick(pagination, PAGINATION_KEYS);
        const requestParams = merge(paginationParams, { viewGroups }, queryParams);
        const headers = { 'X-Splash-Guest-Access-Hash': guestAccessHash };
        return this.apiClient.get(`/event/${eventId}/sessions/feed/${splashFeedId}`, requestParams, { headers });
    }

    /**
     * Sends a GET request to retrieve additional information for a specific session.
     *
     * @param {integer} eventId
     * @param {integer} sessionId
     * @param {array} viewGroups
     */
    getSession(eventId, sessionId, viewGroups = [VIEW_GROUP_BASIC, VIEW_GROUP_DETAILS]) {
        return this.apiClient.get(`/event/${eventId}/session/${sessionId}`, {
            viewGroups,
        });
    }

    /**
     * Sends a GET request to retrieve the distinct session venues on a given event.
     */
    getPublicSessionVenues(eventId, pagination = { page: 1, limit: 10 }) {
        const paginationParams = pick(pagination, PAGINATION_KEYS);
        return this.apiClient.get(`/public/event/${eventId}/sessions/venues`, paginationParams);
    }

    /**
     * Sends a GET request to retrieve the distinct session venues on a given event.
     */
    getSessionVenues(eventId, pagination = { page: 1, limit: 10 }) {
        const paginationParams = pick(pagination, PAGINATION_KEYS);
        return this.apiClient.get(`/event/${eventId}/sessions/venues`, paginationParams);
    }

    /**
     * Sends a GET request to retrieve the distinct session rooms on a given event.
     */
    getPublicSessionRooms(eventId, pagination = { page: 1, limit: 10 }) {
        const paginationParams = pick(pagination, PAGINATION_KEYS);
        return this.apiClient.get(`/public/event/${eventId}/sessions/rooms`, paginationParams);
    }

    /**
     * Sends a GET request to retrieve the distinct session rooms on a given event.
     */
    getSessionRooms(eventId, pagination = { page: 1, limit: 10 }, options = {}) {
        const params = {
            ...pick(pagination, PAGINATION_KEYS),
            ...decamelizeKeys(options),
        };
        return this.apiClient.get(`/event/${eventId}/sessions/rooms`, params);
    }

    /**
     * Sends a GET request to retrieve the distinct session start times on a given event.
     */
    getPublicSessionStartTimes(eventId, pagination = { page: 1, limit: 10 }) {
        const paginationParams = pick(pagination, PAGINATION_KEYS);
        return this.apiClient.get(`/public/event/${eventId}/sessions/times`, paginationParams);
    }

    /**
     * Sends a GET request to retrieve the distinct session start days on a given event.
     */
    getSessionSessionStartDays(eventId, pagination = { page: 1, limit: 10 }) {
        const paginationParams = pick(pagination, PAGINATION_KEYS);
        return this.apiClient.get(`/public/event/${eventId}/sessions/days`, paginationParams);
    }

    /**
     * Sends a GET request to retrieve the distinct session start dates on a given event.
     */
    getPublicSessionStartDates(eventId, pagination = { page: 1, limit: 10 }) {
        const paginationParams = pick(pagination, PAGINATION_KEYS);
        return this.apiClient.get(`/public/event/${eventId}/sessions/dates`, paginationParams);
    }

    /**
     * Sends a GET request to retrieve the distinct session tags on a given event.
     */
    getPublicSessionTagsForEvent(eventId, pagination = { page: 1, limit: 10 }) {
        const paginationParams = pick(pagination, PAGINATION_KEYS);
        return this.apiClient.get(`/public/event/${eventId}/sessions/tags`, paginationParams);
    }

    /**
     * Sends a GET request to retrieve the distinct session tags on a given event.
     */
    getSessionTagsForEvent(eventId, pagination = { page: 1, limit: 10 }, filters) {
        const paginationParams = pick(pagination, PAGINATION_KEYS);
        return this.apiClient.get(`/event/${eventId}/sessions/tags`, {
            ...paginationParams,
            ...decamelizeKeys(filters),
        });
    }

    /**
     * Sends a POST request to create a session.
     *
     * @param {integer} eventId
     * @param {object} payload
     */
    createSession(eventId, payload) {
        return this.apiClient.post(`/event/${eventId}/session`, payload);
    }

    /**
     * Sends a PUT request to update a session.
     *
     * @param {integer} eventId
     * @param {integer} sessionId
     * @param {object} payload
     */
    updateSession(eventId, sessionId, payload) {
        return this.apiClient.put(`/event/${eventId}/session/${sessionId}`, payload);
    }

    /**
     * Sends a POST request to duplicate a session.
     *
     * @param {integer} eventId
     * @param {integer} sessionId
     */
    duplicateSession(eventId, sessionId) {
        return this.apiClient.post(`/event/${eventId}/session/${sessionId}/duplicate`);
    }

    /**
     * Sends a DELETE request to remove a session.
     *
     * @param {integer} eventId
     * @param {integer} sessionId
     */
    deleteSession(eventId, sessionId) {
        return this.apiClient.delete(`/event/${eventId}/session/${sessionId}`);
    }

    /**
     * Sends an POST request to export sessions.
     *
     * @param {integer} eventId
     * @param {object} queryParams
     *
     */
    exportSessions(eventId, queryParams) {
        return this.apiClient.post(`/event/${eventId}/sessions/export`, queryParams);
    }

    /**
     * Sends an POST request to upload a file with sessions.
     *
     * @param {integer} eventId
     * @param {blob} file
     *
     */
    uploadCsvFile(eventId, file) {
        const url = `/event/${eventId}/sessions/upload`;
        return this.apiClient.postFile(url, file);
    }

    /**
     * Sends an POST request to import a file with sessions.
     *
     * @param {integer} eventId
     * @param {object} queryParams
     *
     */
    importCsvFile(eventId, importParams) {
        return this.apiClient.post(`/event/${eventId}/sessions/import`, importParams);
    }

    /**
     * Sends a GET request to retrieve the session stage for an event. This does not
     * always exist.
     *
     * @param {integer} eventId
     */
    getSessionStage(eventId) {
        return this.apiClient.get(`/events/${eventId}/stages`, {
            types: ['session'],
        });
    }
}

export const getSessionApiClient = () => new SessionApiClient(getSplashApiClient());
